/**
 * Settings and platform health.
 *
 * Deliberately the place the platform admits what it is not doing: which integrations are absent
 * so containment runs in simulation, whether an AI analyst is configured, and whether detection
 * is currently degraded. A console that only reports what is working teaches its operators to
 * assume the rest.
 */

import { useState } from 'react'
import { api, type Feed, type Playbook } from '../api'
import { useSession, useTheme } from '../App'
import { Async, Card, Chip, Mono, Note, fmtDuration, useAsync } from '../components/ui'
import { AccountsCard, ChangePasswordCard } from '../components/PasswordCards'

type ActiveSession = {
  principal_id: string
  display_name: string
  roles: string[]
  created_at: number
  expires_at: number
  federated: boolean
}

export default function Settings() {
  const { me } = useSession()
  const [revoking, setRevoking] = useState<string | null>(null)
  const [revoked, setRevoked] = useState<string | null>(null)
  const [theme, setTheme] = useTheme()
  // /api/platform, not /api/health: the open endpoint answers liveness only, because the
  // deployment's internals - including the addresses of internal adapters - are not public.
  const health = useAsync<{
    env: string
    uptime_ms: number
    tenants: number
    persistent: boolean
    model: string
    model_detail: string
    analyst: string
    integrations: string[]
    sandbox: string
    syslog: string[]
    spool: string
    limits: string
    signin: string
    signin_refusals: number
    ingest_refusals: number
    lines_refused: number
    api_refusals: number
  }>(
    () => api.get('/platform'),
    [],
  )
  // Only fetched where the caller may act on it. The endpoint enforces the same capability; this
  // is so the console does not show an administrator's view to somebody who would only get a 403.
  const canManage = me.capabilities.includes('manage_members')
  const sessions = useAsync<ActiveSession[]>(
    () => (canManage ? api.get<ActiveSession[]>('/auth/sessions') : Promise.resolve([])),
    [me.tenant, revoked],
  )
  const feeds = useAsync<Feed[]>(() => api.get<Feed[]>('/intel/feeds'), [me.tenant])
  const playbooks = useAsync<Playbook[]>(() => api.get<Playbook[]>('/playbooks'), [me.tenant])

  const integrations = new Set<string>()
  for (const p of playbooks.data ?? []) for (const i of p.required_integrations) integrations.add(i)

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Settings</h1>
          <div className="sub">Your session, this workspace, and what the platform is not currently able to do.</div>
        </div>
      </div>

      <div className="grid cols-2">
        <Card title="Appearance">
          <div className="field">
            <label>Theme</label>
            <div className="row">
              {(['system', 'light', 'dark'] as const).map((t) => (
                <button key={t} className={theme === t ? 'primary sm' : 'sm'} onClick={() => setTheme(t)}>
                  {t}
                </button>
              ))}
            </div>
            <div className="hint">
              Dark is the default. A console read for eight-hour stretches in a dim room should not
              be a light source.
            </div>
          </div>
        </Card>

        <Card title="Session">
          <dl className="kv">
            <dt>Signed in as</dt>
            <dd>{me.display_name}</dd>
            <dt>Identity</dt>
            <dd className="mono small">{me.id}</dd>
            <dt>Workspace</dt>
            <dd>
              {me.tenant_display_name} <span className="faint mono">({me.tenant})</span>
            </dd>
            <dt>Roles</dt>
            <dd>
              {me.roles.map((r) => (
                <Chip key={r} tone="outline">
                  {r.replace(/_/g, ' ')}
                </Chip>
              ))}
            </dd>
            <dt>You may</dt>
            <dd>
              {me.capabilities.map((c) => (
                <Chip key={c} tone="neutral">
                  {c.replace(/_/g, ' ')}
                </Chip>
              ))}
            </dd>
          </dl>
          {!me.capabilities.includes('approve_action') && (
            <Note>
              You cannot grant approvals. That is a separate capability from working an incident, so
              the person who proposes a wide change is not automatically the one who authorizes it.
            </Note>
          )}
        </Card>

        <ChangePasswordCard />

        {canManage && (
          <Card
            title="Signed in now"
            subtitle="Roles are read at sign-in and not re-checked, so ending a session is how access is actually taken away"
          >
            <Async state={sessions}>
              {(rows) => (
                <>
                  {rows.length === 0 ? (
                    <div className="faint small">Nobody else is signed in to this workspace.</div>
                  ) : (
                    /* `table-wrap` is what every other table here uses: it makes a narrow
                       screen scroll the table rather than the whole page. */
                    <div className="table-wrap">
                      <table>
                      <thead>
                        <tr>
                          <th>Who</th>
                          <th>Roles</th>
                          <th>Signed in</th>
                          <th />
                        </tr>
                      </thead>
                      <tbody>
                        {rows.map((r) => (
                          <tr key={r.principal_id}>
                            <td>
                              {r.display_name}
                              <div className="faint mono small">{r.principal_id}</div>
                            </td>
                            <td>
                              {r.roles.map((role) => (
                                <Chip key={role} tone="outline">
                                  {role.replace(/_/g, ' ')}
                                </Chip>
                              ))}
                              {r.federated && <Chip tone="neutral">sso</Chip>}
                            </td>
                            <td className="small faint">
                              {fmtDuration(Date.now() - r.created_at)} ago
                            </td>
                            <td>
                              {r.principal_id === me.id ? (
                                <span className="faint small">you</span>
                              ) : (
                                <button
                                  disabled={revoking === r.principal_id}
                                  onClick={() => {
                                    setRevoking(r.principal_id)
                                    api
                                      .post('/auth/sessions/revoke', {
                                        principal_id: r.principal_id,
                                      })
                                      .then(() => setRevoked(r.principal_id + Date.now()))
                                      .finally(() => setRevoking(null))
                                  }}
                                >
                                  {revoking === r.principal_id ? 'Ending…' : 'End sessions'}
                                </button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    </div>
                  )}
                  <Note>
                    Sessions are held in the control plane's memory, so this ends them on this node.
                    There is no directory sync: nothing here notices when somebody is removed from a
                    group at the identity provider, which is why this button exists.
                  </Note>
                </>
              )}
            </Async>
          </Card>
        )}

        {canManage && <AccountsCard />}

        <Card title="Control plane">
          <Async state={health}>
            {(h) => (
              <dl className="kv">
                <dt>Environment</dt>
                <dd>
                  <Chip tone={h.env === 'prod' ? 'ok' : 'warn'}>{h.env}</Chip>
                  {h.env !== 'prod' && (
                    <span className="faint small">
                      {' '}
                      — seeded development accounts are enabled
                    </span>
                  )}
                </dd>
                <dt>Uptime</dt>
                <dd>{fmtDuration(h.uptime_ms)}</dd>
                <dt>Workspaces</dt>
                <dd className="tnum">{h.tenants}</dd>
                <dt>AI advisor</dt>
                <dd>
                  {h.model ? (
                    <>
                      <Chip tone="ok">{h.model}</Chip>
                      <div className="faint small">{h.model_detail}</div>
                    </>
                  ) : (
                    <>
                      <Chip tone="warn">none</Chip>
                      <span className="faint small">
                        {' '}
                        — no API key is set; assessments are deterministic summaries
                      </span>
                    </>
                  )}
                </dd>
                <dt>Autonomous analyst</dt>
                <dd>
                  {h.analyst && h.analyst !== 'disabled' ? (
                    <>
                      <Chip tone="ok">running</Chip>
                      <div className="faint small">
                        {h.analyst} — it posts assessments into incident channels and can never
                        approve anything.
                      </div>
                    </>
                  ) : (
                    <>
                      <Chip tone="neutral">disabled</Chip>
                      <span className="faint small">
                        {' '}
                        — incidents still open channels and run playbooks; nothing writes an
                        assessment into them.
                      </span>
                    </>
                  )}
                </dd>
                <dt>Malware detonation</dt>
                <dd>
                  {h.sandbox ? (
                    <>
                      <Chip tone="ok">configured</Chip>
                      <div className="faint small">{h.sandbox}</div>
                    </>
                  ) : (
                    <>
                      <Chip tone="warn">static only</Chip>
                      <span className="faint small">
                        {' '}
                        — samples are analyzed but never detonated, so a verdict can reach
                        <em> suspicious</em> but never <em>clean</em>
                      </span>
                    </>
                  )}
                </dd>
                <dt>Syslog receivers</dt>
                <dd>
                  {(h.syslog ?? []).length === 0 ? (
                    <>
                      <Chip tone="neutral">none</Chip>
                      <span className="faint small">
                        {' '}
                        — lines arrive over HTTP only, with an ingest credential
                      </span>
                    </>
                  ) : (
                    <ul className="small" style={{ margin: 0, paddingLeft: 18 }}>
                      {(h.syslog ?? []).map((line) => (
                        <li key={line}>
                          <Mono>{line}</Mono>
                        </li>
                      ))}
                    </ul>
                  )}
                </dd>
                <dt>Sign-in throttle</dt>
                <dd>
                  {h.signin?.includes('off') ? (
                    <>
                      <Chip tone="danger">off</Chip>
                      <span className="faint small">
                        {' '}
                        — password guessing is bounded by nothing, and each attempt costs this
                        process real work
                      </span>
                    </>
                  ) : (
                    <>
                      <Chip tone="ok">on</Chip>
                      <div className="faint small">{h.signin}</div>
                    </>
                  )}
                  {h.signin_refusals > 0 && (
                    <div className="small" style={{ marginTop: 6 }}>
                      <Chip tone="warn">
                        {h.signin_refusals} attempt{h.signin_refusals === 1 ? '' : 's'} refused
                      </Chip>{' '}
                      <span className="faint">
                        since this process started. Expected if somebody mistyped a password;
                        worth looking at if it keeps climbing.
                      </span>
                    </div>
                  )}
                </dd>
                <dt>Rate limits</dt>
                <dd>
                  {h.limits?.includes('unlimited') && !h.limits?.includes('/s') ? (
                    <>
                      <Chip tone="neutral">none</Chip>
                      <span className="faint small">
                        {' '}
                        — one workspace can consume the capacity every workspace shares
                      </span>
                    </>
                  ) : (
                    <>
                      <Chip tone="ok">per workspace</Chip>
                      <div className="faint small">{h.limits}</div>
                    </>
                  )}
                  {(h.ingest_refusals > 0 || h.api_refusals > 0) && (
                    <div className="small" style={{ marginTop: 6 }}>
                      <Chip tone="warn">
                        {h.lines_refused} line{h.lines_refused === 1 ? '' : 's'} refused
                      </Chip>{' '}
                      <span className="faint">
                        in {h.ingest_refusals} batch{h.ingest_refusals === 1 ? '' : 'es'}
                        {h.api_refusals > 0
                          ? `, and ${h.api_refusals} API request${h.api_refusals === 1 ? '' : 's'}`
                          : ''}
                        , since this process started. Refused batches were not accepted in part;
                        a shipper that retries loses nothing.
                      </span>
                    </div>
                  )}
                </dd>
                <dt>Ingest spool</dt>
                <dd>
                  {h.spool ? (
                    <>
                      <Chip tone={h.spool.includes('NOTHING IS PERSISTED') ? 'warn' : 'ok'}>
                        enabled
                      </Chip>
                      <div className="faint small">{h.spool}</div>
                    </>
                  ) : (
                    <>
                      <Chip tone="neutral">none</Chip>
                      <span className="faint small">
                        {' '}
                        — received lines queue in memory; a restart loses whatever is queued
                      </span>
                    </>
                  )}
                </dd>
                <dt>Storage</dt>
                <dd>
                  {h.persistent ? (
                    <>
                      <Chip tone="ok">PostgreSQL</Chip>
                      <span className="faint small">
                        {' '}
                        — the estate is hydrated at startup and survives a restart
                      </span>
                    </>
                  ) : (
                    <>
                      <Chip tone="warn">in memory</Chip>
                      <span className="faint small">
                        {' '}
                        — no BASTION_DATABASE_URL is set, so state is lost on restart
                      </span>
                    </>
                  )}
                </dd>
              </dl>
            )}
          </Async>
        </Card>

        <Card title="What is not configured" subtitle="The platform's own account of its gaps">
          <div className="stack" style={{ gap: 10 }}>
            <div>
              <strong className="small">AI analyst</strong>
              <div className="small muted">
                {health.data?.model ? (
                  <>
                    Configured. Assessments and onboarding commentary come from{' '}
                    <Mono>{health.data.model}</Mono>, which <em>proposes</em> only — every drafted
                    rule is checked by the validator and saved by a person before it can fire, and
                    a model outage falls back to the deterministic summaries rather than failing.
                  </>
                ) : (
                  <>
                    No model is configured, so incident assessments are deterministic summaries of
                    the recorded evidence rather than an analyst's reading of it, and onboarding
                    recommendations carry the profiler's reasoning rather than product-specific
                    commentary. Everything still works; it explains less.
                  </>
                )}
              </div>
            </div>

            <div>
              <strong className="small">Response integrations</strong>
              {integrations.size === 0 ? (
                <div className="small muted">No playbook requires an integration.</div>
              ) : (
                <>
                  <div className="row wrap" style={{ margin: '4px 0' }}>
                    {[...integrations].map((i) => {
                      const live = (health.data?.integrations ?? []).some((line) =>
                        line.startsWith(`${i} -> `),
                      )
                      return (
                        <Chip key={i} tone={live ? 'ok' : 'warn'}>
                          {i} — {live ? 'configured' : 'not configured'}
                        </Chip>
                      )
                    })}
                  </div>
                  {(health.data?.integrations ?? []).length > 0 && (
                    <ul className="small muted" style={{ margin: '4px 0 6px', paddingLeft: 18 }}>
                      {(health.data?.integrations ?? []).map((line) => (
                        <li key={line}>
                          <Mono>{line}</Mono>
                        </li>
                      ))}
                    </ul>
                  )}
                  <div className="small muted">
                    Containment steps needing an integration that is not configured run in
                    simulation, say so in the incident channel, and are recorded on the run as
                    steps that were never real. Nothing is changed on the estate — a platform that
                    reported containing a host it never touched would be worse than one that admits
                    it did not.
                  </div>
                </>
              )}
            </div>

            <div hidden={!!health.data?.sandbox}>
              <strong className="small">Malware detonation</strong>
              <div className="small muted">
                No sandbox runner is configured. Static analysis is real and runs here; a
                static-only verdict can reach <em>suspicious</em> but never <em>clean</em>.
              </div>
            </div>

            <div>
              <strong className="small">Intelligence feeds</strong>
              <Async state={feeds}>
                {(list) => {
                  const ok = list.filter((f) => f.status === 'ok')
                  const needsKey = list.filter((f) => f.status === 'not_configured')
                  const scaffold = list.filter((f) => f.scaffold)
                  return (
                    <div className="small muted">
                      {ok.length} active, {needsKey.length} awaiting a credential, {scaffold.length}{' '}
                      registered but not implemented.
                    </div>
                  )
                }}
              </Async>
            </div>
          </div>
        </Card>
      </div>
    </>
  )
}
